import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "../services/auth.service";
import { environment } from "../../environments/environment";
import { JsonPipe } from "@angular/common";
import { CodeBlock } from "../shared/code-block/code-block";

@Component({
  selector: "app-login",
  imports: [JsonPipe, CodeBlock],
  templateUrl: "./login.html",
  styleUrls: ["./login.scss"],
  host: { class: "mds-page mds-container" },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login implements OnInit {
  readonly authService = inject(AuthService);
  readonly router = inject(Router);

  protected readonly email = signal("");
  protected readonly hintMessage = signal("");
  protected readonly hintAppearance = signal("");
  protected readonly hintVisible = signal(false);
  protected readonly checking = signal(false);
  protected readonly directVisible = signal(false);
  protected readonly lastHint = signal<{ available?: boolean; enforced?: boolean } | null>(null);

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    try {
      const user = await this.authService.getUser();
      if (user && !user.expired) {
        window.location.href = "/callback.html";
      }
    } catch {
      // ignore
    }
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.email.set(value);
    this.hintVisible.set(false);
    this.directVisible.set(false);
    this.lastHint.set(null);

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (value.includes("@")) {
      this.debounceTimer = setTimeout(() => this.checkHint(), 600);
    }
  }

  async checkHint(): Promise<void> {
    const emailValue = this.email().trim();
    if (!emailValue) return;

    const domain = emailValue.split("@")[1];
    if (!domain) {
      this.showHint("Enter a valid email address with a domain.", "error");
      return;
    }

    this.checking.set(true);
    try {
      const url = `${environment.authority}${environment.hintEndpoint}?client_id=${encodeURIComponent(environment.clientId)}&domain=${encodeURIComponent(domain)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const hint = await res.json();
      this.lastHint.set(hint);

      if (hint.available && hint.enforced) {
        this.showHint(`SSO is available and enforced for ${domain}`, "info");
        this.directVisible.set(true);
      } else if (hint.available) {
        this.showHint(`SSO is available for ${domain}`, "success");
        this.directVisible.set(true);
      } else {
        this.showHint(`SSO is not available for ${domain}`, "error");
        this.directVisible.set(true);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      this.showHint(`Failed to check SSO: ${message}`, "error");
    } finally {
      this.checking.set(false);
    }
  }

  async startLogin(): Promise<void> {
    try {
      await this.authService.loginWithOptions({ extraQueryParams: { login_hint: this.email() } });
    } catch (e: unknown) {
      console.error("signinRedirect failed", e);
    }
  }

  private showHint(msg: string, appearance: string): void {
    this.hintMessage.set(msg);
    this.hintAppearance.set(appearance);
    this.hintVisible.set(true);
  }
}